import React from "react";
import { Box, CircularProgress } from "@mui/joy";
import DarkTypography from "components/common/DarkTypography";
import CommonModal from "components/common/CommonModal";
import IconButtonCTA from "components/common/IconButtonCTA";
import { colorTokens } from "styles/globalStyles";
import type { MultiComment } from "api/zekay/comments";

import ExcursionModalFormFields from "./ExcursionModalFormFields";
import ExcursionGroupCard from "./ExcursionGroupCard";
import ProfileForm from "components/shared/profileForm/ProfileForm";
import { ExcursionTemplateLimitsChips } from "./ExcursionCountWithMaxChip";
import CommentsModal from "components/common/CommentsModal";
import ExcursionModalHeader from "./ExcursionModalHeader";
import { ParamsFieldsRenderer } from "./ParamsFieldsRenderer";
import CustomerOverviewModal from "components/shared/customerOverview/CustomerOverviewModal";
import { personName } from "../../../excursions/shared/utils/excursionUtils";
import { normalizeCustomerEmoji } from "../../../common/customerEmoji";
import ExcursionValidateCountersModal from "./ExcursionValidateCountersModal";

import type { ExcursionModalProps } from "../excursionModalTypes";
export type { ExcursionFormPayload, ExcursionModalUser } from "../excursionModalTypes";

import { ExcursionStatusCancelled } from "api/excursions/Excursion.typed";
import { groupCustomersList, newExcursionCreateCustomerDraft } from "../model/groupModel";
import { useExcursionModalOrchestration } from "../hooks/useExcursionModalOrchestration";

import { SDelete, SCreate, SRedirect, SSms } from "styles/StyledMUI";
import ExcursionSmsModal, {
  buildExcursionSmsRecipients,
  smsCustomerColors,
  type ExcursionSmsSendGroup,
} from "./ExcursionSmsModal";
import { usePermissions } from "contexts/app/permissions";

export default function ExcursionModal(props: ExcursionModalProps) {
  const {
    open,
    onClose,
    initialExcursion,
    isReadonly,
    onDelete,
    name,
    loading,
    validateCountersOpen,
    modalTitle,
    canSaveExcursion,
    handleSubmit,
    canEditExcursionDetails,
    canEditExcursionStatus,
    cancellationReason,
    setCancellationReason,
    dataLoading,
    excursionColor,
    setName,
    setExcursionColor,
    status,
    handleHeaderStatusChange,
    excursionTemplatesList,
    selectedExcursionTemplateId,
    handleExcursionTemplateChange,
    date,
    setDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    paramsTemplate,
    params,
    setParams,
    searchUsersForParams,
    searchGuidesForParams,
    searchCustomersForParams,
    modalTotals,
    groups,
    getGroupParamsTemplateForIndex,
    setGroupParams,
    groupTemplatesList,
    defaultGroupTemplateId,
    setGroupTemplateId,
    getGroupLimitsForIndex,
    bookingStatusMap,
    userSearch,
    setUserSearch,
    openUserSearch,
    openClientSearch,
    filteredUsers,
    clientSearch,
    setClientSearch,
    setGroupAddUser,
    setGroupCustomer,
    setCreateCustomerModalGroupIndex,
    setCreateCustomerDraft,
    setGroupRemoveUser,
    setGroupRemoveClient,
    setGroupColor,
    setGroupUserColor,
    setGroupClientColor,
    setGroupUserComments,
    setGroupClientComments,
    commentsModalGroupIndex,
    setCommentsModalGroupIndex,
    handleRemoveGroup,
    handleClientDropOnGroup,
    handleExportPdf,
    isEditing,
    createCustomerDraft,
    createCustomerModalGroupIndex,
    excursionCreateProfileSetter,
    noopCustomerUpdated,
    isDesync,
    currentUserName,
    commentsForModal,
    setCommentsForModal,
    setGroupComments,
    updateExcursionGroupComments,
    setValidateCountersOpen,
    counterValidationGroupSlices,
    handleValidateCountersSuccess,
    canOpenCustomerOverview,
    setCustomerOverviewCustomerId,
    customerOverviewCustomerId,
    handleCustomerOverviewUpdated,
    handleDeleteClick,
    addGroup,
    canAddMoreGroups,
    users,
    customersForLabels,
  } = useExcursionModalOrchestration(props);

  const { hasBackendPermission } = usePermissions();
  const canSendSMS = hasBackendPermission("excursions.send_sms");
  const [smsOpen, setSmsOpen] = React.useState(false);

  const smsRecipients = React.useMemo(() => {
    const userIds: number[] = [];
    const customerIds: number[] = [];

    for (const g of groups) {
      for (const u of g.users ?? []) {
        if (u.id > 0) userIds.push(u.id);
      }
      for (const c of groupCustomersList(g)) {
        if (c.id > 0) customerIds.push(c.id);
      }
    }

    const collectPicklistIds = (value: unknown, into: number[]) => {
      if (!Array.isArray(value)) return;
      for (const item of value) {
        if (item && typeof item === "object" && "id" in item) {
          const id = Number((item as { id: unknown }).id);
          if (Number.isFinite(id) && id > 0) into.push(id);
        }
      }
    };

    for (const [key, value] of Object.entries(params ?? {})) {
      const k = key.toLowerCase();
      if (k.includes("customer") || k.includes("client")) collectPicklistIds(value, customerIds);
      else collectPicklistIds(value, userIds);
    }

    return buildExcursionSmsRecipients({
      userIds,
      customerIds,
      users,
      customers: customersForLabels,
    });
  }, [groups, params, users, customersForLabels]);

  const smsSendGroups = React.useMemo<ExcursionSmsSendGroup[]>(() => {
    if (initialExcursion?.id == null) return [];
    const start = startTime?.trim() || "";
    const end = endTime?.trim() || "";
    const base = name.trim() || `Sortie #${initialExcursion.id}`;
    const label = start && end ? `${base} · ${start}–${end}` : start ? `${base} · ${start}` : base;

    return [
      {
        excursionId: initialExcursion.id,
        label,
        userIds: smsRecipients.filter((r) => r.kind === "user").map((r) => r.id),
        customerIds: smsRecipients.filter((r) => r.kind === "customer").map((r) => r.id),
        customerColors: smsCustomerColors(
          groups.map((g) => ({
            color: g.color,
            customers: groupCustomersList(g),
          })),
        ),
      },
    ];
  }, [endTime, groups, initialExcursion?.id, name, smsRecipients, startTime]);

  const openCustomerOverview = React.useMemo(
    () =>
      canOpenCustomerOverview
        ? (customerId: number) => {
            setCustomerOverviewCustomerId(customerId);
          }
        : undefined,
    [canOpenCustomerOverview, setCustomerOverviewCustomerId],
  );

  const smsActions =
    canSendSMS && initialExcursion?.id != null ? (
      <>
        <IconButtonCTA
          aria-label="Envoyer un SMS"
          ctaVariant="accent"
          size="sm"
          onClick={() => setSmsOpen(true)}
          disabled={smsRecipients.length === 0}
        >
          <SSms />
        </IconButtonCTA>
      </>
    ) : null;

  return (
    <>
      <CommonModal
        open={open && !validateCountersOpen}
        onClose={onClose}
        title={modalTitle}
        onConfirm={canSaveExcursion ? handleSubmit : undefined}
        confirmLabel="Enregistrer"
        submitDisabled={!name.trim() || !canSaveExcursion}
        loading={loading}
        size="xl"
        fitContentHeight
        disableEnforceFocus
        footerActions={
          isReadonly ? (
            (smsActions ?? undefined)
          ) : (
            <>
              {smsActions}
              <IconButtonCTA
                aria-label="Exporter en PDF"
                ctaVariant="accent"
                size="sm"
                onClick={handleExportPdf}
                disabled={!name.trim()}
              >
                <SRedirect />
              </IconButtonCTA>
              {isEditing && onDelete && canEditExcursionDetails && (
                <IconButtonCTA
                  aria-label="Supprimer la sortie"
                  ctaVariant="danger"
                  size="sm"
                  onClick={handleDeleteClick}
                  disabled={loading}
                >
                  <SDelete />
                </IconButtonCTA>
              )}
            </>
          )
        }
      >
        {dataLoading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}
          >
            <CircularProgress
              size="lg"
              sx={{
                "--CircularProgress-trackColor": colorTokens.border.default,
                "--CircularProgress-progressColor": colorTokens.accent.primary,
              }}
            />
          </Box>
        ) : (
          <>
            <ExcursionModalHeader
              name={name}
              onNameChange={setName}
              color={excursionColor}
              onColorChange={setExcursionColor}
              status={status}
              onStatusChange={handleHeaderStatusChange}
              canEditDetails={canEditExcursionDetails}
              canEditStatus={canEditExcursionStatus}
              cancellationReason={cancellationReason}
              onCancellationReasonChange={setCancellationReason}
              showCancellationMotif={status === ExcursionStatusCancelled}
              canEditCancellationMotif={canSaveExcursion && status === ExcursionStatusCancelled}
              showStatus={!!initialExcursion}
              excursionTemplates={
                excursionTemplatesList.length >= 1 ? excursionTemplatesList : undefined
              }
              selectedExcursionTemplateId={selectedExcursionTemplateId}
              onExcursionTemplateChange={handleExcursionTemplateChange}
            />
            <ExcursionModalFormFields
              date={date}
              onDateChange={setDate}
              startTime={startTime}
              onStartTimeChange={setStartTime}
              endTime={endTime}
              onEndTimeChange={setEndTime}
              canEdit={canEditExcursionDetails}
            />
            {Object.keys(paramsTemplate).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <ParamsFieldsRenderer
                  paramsTemplate={paramsTemplate}
                  params={params}
                  onChange={setParams}
                  disabled={!canEditExcursionDetails}
                  gap={1.5}
                  searchUsers={searchUsersForParams}
                  searchCustomers={searchCustomersForParams}
                  searchGuides={searchGuidesForParams}
                />
              </Box>
            )}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
                <DarkTypography level="body-sm" colorVariant="legend">
                  Groupes
                </DarkTypography>
                {modalTotals.show && (
                  <ExcursionTemplateLimitsChips
                    totalPeople={modalTotals.totalPeople}
                    groupCount={modalTotals.groupCount}
                    maxPeople={modalTotals.maxPeople}
                    maxGroups={modalTotals.maxGroups}
                    size="sm"
                  />
                )}
              </Box>
              {groups.map((g, index) => (
                <Box
                  key={g.local_key}
                  id={`zekay-excursion-group-${g.local_key}`}
                  tabIndex={-1}
                  sx={{ outline: "none" }}
                >
                  <ExcursionGroupCard
                    index={index}
                    group={g}
                    canEdit={canEditExcursionDetails}
                    paramsTemplate={getGroupParamsTemplateForIndex(index)}
                    onParamsChange={(nextParams) => setGroupParams(index, nextParams)}
                    searchUsersForParams={searchUsersForParams}
                    searchCustomersForParams={searchCustomersForParams}
                    searchGuidesForParams={searchGuidesForParams}
                    onOpenCustomerOverview={openCustomerOverview}
                    groupTemplates={groupTemplatesList.length >= 1 ? groupTemplatesList : undefined}
                    selectedGroupTemplateId={g.paramsTemplateId ?? defaultGroupTemplateId}
                    onGroupTemplateChange={
                      groupTemplatesList.length >= 1
                        ? (id) => setGroupTemplateId(index, id)
                        : undefined
                    }
                    maxUsersPerGroup={getGroupLimitsForIndex(index).maxUsersPerGroup}
                    maxCustomersPerGroup={getGroupLimitsForIndex(index).maxCustomersPerGroup}
                    bookingStatusMap={bookingStatusMap}
                    userSearchActive={userSearch?.groupIndex === index}
                    userSearchQuery={userSearch?.groupIndex === index ? userSearch.query : ""}
                    onUserSearchChange={(q) => setUserSearch({ groupIndex: index, query: q })}
                    onUserSearchFocus={() => openUserSearch(index)}
                    onUserSearchClose={() => setUserSearch(null)}
                    filteredUsers={filteredUsers}
                    onSelectUser={(u) => {
                      setGroupAddUser(index, u);
                      setUserSearch(null);
                    }}
                    clientSearchActive={clientSearch?.groupIndex === index}
                    clientSearchQuery={clientSearch?.groupIndex === index ? clientSearch.query : ""}
                    clientSearchResults={
                      clientSearch?.groupIndex === index
                        ? clientSearch.results.filter((customer) => {
                            if (customer.Id == null) return false;
                            return !groups.some(
                              (grp, grpIdx) =>
                                grpIdx !== index &&
                                groupCustomersList(grp).some((c) => c.id === customer.Id),
                            );
                          })
                        : []
                    }
                    onClientSearchChange={(q) =>
                      setClientSearch({
                        groupIndex: index,
                        query: q,
                        results: clientSearch?.groupIndex === index ? clientSearch.results : [],
                      })
                    }
                    onClientSearchFocus={() => openClientSearch(index)}
                    onClientSearchClose={() => setClientSearch(null)}
                    onSelectClient={(c) => {
                      setGroupCustomer(index, c);
                      setClientSearch(null);
                    }}
                    onCreateClientClick={() => {
                      setCreateCustomerModalGroupIndex(index);
                      setCreateCustomerDraft(newExcursionCreateCustomerDraft());
                    }}
                    onRemoveUser={(userIndex) => setGroupRemoveUser(index, userIndex)}
                    onRemoveClient={(clientIndex) => setGroupRemoveClient(index, clientIndex)}
                    onColorChange={(hex) => setGroupColor(index, hex)}
                    onUserColorChange={(userIndex, hex) => setGroupUserColor(index, userIndex, hex)}
                    onClientColorChange={(clientIndex, hex) =>
                      setGroupClientColor(index, clientIndex, hex)
                    }
                    onUserCommentsChange={(userIndex, cmts) =>
                      setGroupUserComments(index, userIndex, cmts)
                    }
                    onClientCommentsChange={(clientIndex, cmts) =>
                      setGroupClientComments(index, clientIndex, cmts)
                    }
                    onCommentsClick={() => setCommentsModalGroupIndex(index)}
                    commentsCount={g.comments?.length ?? 0}
                    onRemoveGroup={handleRemoveGroup(index)}
                    onDragOver={
                      canEditExcursionDetails
                        ? (e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                          }
                        : undefined
                    }
                    onDrop={
                      canEditExcursionDetails
                        ? (e) => {
                            e.preventDefault();
                            try {
                              const d = JSON.parse(e.dataTransfer.getData("application/json")) as {
                                type: string;
                                groupIndex?: number;
                                clientIndex?: number;
                                customer_id?: number;
                                customer_label?: string;
                                customer_last_name?: string;
                                customer_first_name?: string;
                                customer_qualification?: string;
                                customer_color?: string | null;
                                customer_comments?: MultiComment[];
                                customer_emoji?: string | null;
                              };
                              if (
                                d.type === "client" &&
                                typeof d.groupIndex === "number" &&
                                typeof d.customer_id === "number" &&
                                d.customer_label != null &&
                                typeof d.clientIndex === "number"
                              ) {
                                handleClientDropOnGroup(
                                  index,
                                  d.groupIndex,
                                  d.clientIndex,
                                  d.customer_id,
                                  d.customer_label,
                                  {
                                    lastName: d.customer_last_name,
                                    firstName: d.customer_first_name,
                                    qualification: d.customer_qualification,
                                  },
                                  d.customer_color,
                                  d.customer_comments,
                                  d.customer_emoji,
                                );
                              }
                            } catch (_) {
                              /* ignore */
                            }
                          }
                        : undefined
                    }
                    onClientDragStart={
                      canEditExcursionDetails
                        ? (ev, clientIndex) => {
                            const list = groupCustomersList(g);
                            const client = list[clientIndex];
                            if (!client) return;
                            const dragEmoji = normalizeCustomerEmoji(client.emoji ?? undefined);
                            ev.dataTransfer.setData(
                              "application/json",
                              JSON.stringify({
                                type: "client",
                                groupIndex: index,
                                clientIndex,
                                customer_id: client.id,
                                customer_label: personName(
                                  client.first_name,
                                  client.last_name,
                                  client.id,
                                ),
                                customer_last_name: client.last_name,
                                customer_first_name: client.first_name,
                                customer_qualification: client.qualification,
                                customer_color: client.color ?? null,
                                customer_comments: client.comments,
                                ...(dragEmoji ? { customer_emoji: dragEmoji } : {}),
                              }),
                            );
                            ev.dataTransfer.effectAllowed = "move";
                          }
                        : undefined
                    }
                  />
                </Box>
              ))}
              {canEditExcursionDetails && (
                <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 0.5 }}>
                  <IconButtonCTA
                    size="sm"
                    variant="soft"
                    aria-label="Ajouter un groupe"
                    ctaVariant="accent"
                    onClick={addGroup}
                    disabled={!canAddMoreGroups}
                  >
                    <SCreate />
                  </IconButtonCTA>
                </Box>
              )}
            </Box>
          </>
        )}
      </CommonModal>
      {createCustomerDraft != null && createCustomerModalGroupIndex != null && (
        <ProfileForm
          customer={createCustomerDraft}
          setProfileFormOpen={excursionCreateProfileSetter}
          setCustomerUpdated={noopCustomerUpdated}
          editable={true}
          desync={isDesync}
          closeAfterCreate
          onCustomerCreated={(created) => {
            const idx = createCustomerModalGroupIndex;
            if (created.Id != null && idx != null) setGroupCustomer(idx, created);
          }}
        />
      )}
      <CommentsModal
        open={commentsModalGroupIndex != null}
        title={
          commentsModalGroupIndex != null
            ? `Commentaires — Groupe ${commentsModalGroupIndex + 1}`
            : "Commentaires"
        }
        currentUserName={currentUserName}
        multiComments={commentsForModal}
        setMultiComments={setCommentsForModal}
        onSave={(updated) => {
          if (commentsModalGroupIndex != null && updated != null) {
            setGroupComments(commentsModalGroupIndex, updated);
            const excursionId = initialExcursion?.id;
            const group = groups[commentsModalGroupIndex];
            if (excursionId != null && group?.id != null) {
              updateExcursionGroupComments(excursionId, group.id, updated);
            }
          }
        }}
        onClose={() => setCommentsModalGroupIndex(null)}
        allowEdit={canEditExcursionDetails}
      />
      {customerOverviewCustomerId != null && (
        <CustomerOverviewModal
          open
          onClose={() => setCustomerOverviewCustomerId(null)}
          customerId={customerOverviewCustomerId}
          desync={isDesync}
          onCustomerUpdated={handleCustomerOverviewUpdated}
        />
      )}
      <ExcursionValidateCountersModal
        open={validateCountersOpen}
        onClose={() => setValidateCountersOpen(false)}
        excursionId={initialExcursion?.id}
        excursionName={name}
        excursionDate={date}
        startTime={startTime}
        endTime={endTime}
        customerGroups={counterValidationGroupSlices}
        onSuccess={handleValidateCountersSuccess}
        onOpenCustomerOverview={openCustomerOverview}
      />
      {smsSendGroups.length > 0 && (
        <ExcursionSmsModal
          open={smsOpen}
          onClose={() => setSmsOpen(false)}
          sendGroups={smsSendGroups}
          initialRecipients={smsRecipients}
          previewContext={{
            excursionName: name,
            date,
            startTime,
            endTime,
          }}
        />
      )}
    </>
  );
}
